// `extension-alert-candidates-slim.tpl.html:170` — one candidate in the slim list.
//
// The row every reference capture of the shortlist is mostly made of: a checkbox, the last
// message and CV dates across the top, then the name with its algorithm score, job title,
// company and location.
//
// The hover strip (`extension-alert-entities-profile-actions`, six actions) and the
// `shortlist-hover-open-resume` chevron ARE here now. The note that stood in their place said
// they would follow in slice 3; `ProfileActionsStrip` landed with the snippets and the row
// was never given it, so hovering a row drew the highlight and nothing inside it.
//
// The `<checkbox>` IS here now (`WooCheckbox`): it is a vendored directive whose compiled
// template is three elements, and the stub that stood in for it — a bare div with the caller's
// class — drew no box at all.
import type { ReactElement } from 'react'
import { timeSince } from '../common/timeSince.ts'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { ProfileActionsStrip } from './ProfileActionsStrip.tsx'
import type { ProfileActionDefinition } from './profileActions.ts'
import { candidateName, lastMessageDisplay, trimCandidateName } from './candidateName.ts'

/** The fields the row reads, from the live `getCandidatesByAlertId` response. */
export interface SlimCandidate {
  id: number
  /** ⚠️ OFTEN ABSENT — see `candidateName`, which the row goes through rather than reading this. */
  name?: string | null
  externalId?: string | null
  /** The match score, shown in brackets after the name when `showAlgorithm`. */
  algorithm?: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  /** Epoch millis. */
  cvDate?: number | null
  /** Epoch millis of the last message received; "Never" when there is none. */
  lastReceivedMessage?: number | null
  addedToJob?: boolean
  exported?: boolean
  hidden?: boolean
  viewed?: boolean
}

export interface CandidateRowProps {
  candidate: SlimCandidate
  checked?: boolean
  /** `ctrl.selectedCandidateId` — the row last opened keeps a marker. */
  previouslySelected?: boolean
  /** `messageCandidatesEnabled` — the whole message cell is behind it. */
  messagingEnabled?: boolean
  /** `ctrl.showAlgorithm` — a debug/whitelabel flag, not always on. */
  showAlgorithm?: boolean
  onOpen?: () => void
  onCheck?: (checked: boolean) => void
  /**
   * The envelope — opens `MessageHistoryTooltip`. Called only when there IS a last message;
   * the click is swallowed either way.
   */
  onOpenMessages?: () => void
  /** `now` flows in so a list of rows shares one clock and tests are deterministic. */
  now?: number
  /**
   * The six hover actions — `extension-alert-entities-profile-actions max-action-amount="6"`.
   *
   * ⚠️ WITHOUT THESE THE ROW HAS NO HOVER STATE WORTH THE NAME. The stylesheet reveals
   * `.shortlist-candidate-slim-hover` on `:hover`, so a row that renders none draws the
   * highlight and nothing else — which is how it looked beside build 354, where the same
   * hover shows download, add-to-job, message, share, favour and archive.
   */
  actions?: readonly ProfileActionDefinition[]
  onAction?: (action: ProfileActionDefinition) => void
  /** `ctrl.previewCandidateResume(...)` — the `›` on the right edge. */
  onPreviewResume?: () => void
  /**
   * `!$state.current.data.isCheckboxDependent` gates BOTH the strip and the chevron: while a
   * bulk operation owns the frame the row offers no per-candidate action.
   */
  checkboxDependent?: boolean
}

export function CandidateRow({
  candidate,
  checked = false,
  previouslySelected = false,
  messagingEnabled = false,
  showAlgorithm = false,
  onOpen,
  onCheck,
  onOpenMessages,
  now,
  actions,
  onAction,
  onPreviewResume,
  checkboxDependent = false,
}: CandidateRowProps): ReactElement {
  return (
    <div
      className={[
        'shortlist-candidate-slim',
        `cand-${String(candidate.id)}`,
        checked ? 'checked' : '',
        previouslySelected ? 'previously-selected' : '',
        candidate.hidden === true ? 'is-hidden' : '',
        candidate.viewed === true ? 'is-viewed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* `shortlist-hover-open-resume` — the `›` the stylesheet slides in from the right edge
          on hover. It opens the resume preview, which is its own route. */}
      {/* ⚠️ IT IS NOT CONDITIONAL ON A HANDLER ANY MORE, and the earlier reading of
          `previewCandidateResume` is why it was. That function is not an inline preview with
          no route: it ends in `$state.go(snippetState, { alertId, candidateId, algoScore })`,
          and `snippetState` is `HunterReviewCandidateProfileMore` — the `/more` route this
          port already serves, and already the destination of the row's own click. So the
          chevron has somewhere real to go; withholding it left production's hover affordance
          missing instead. `ng-if` upstream is the checkbox-dependent state alone. */}
      {checkboxDependent ? null : (
        <div className="shortlist-hover-open-resume">
          <a
            className="open-resume-action-btn"
            href=""
            onClick={(event) => {
              event.preventDefault()
              // ⚠️ STOPPED, or the row's own `onOpen` fires too and the click both previews
              // the CV and navigates to the profile.
              event.stopPropagation()
              onPreviewResume?.()
            }}
          >
            <i className="fal fa-chevron-right" />
          </a>
        </div>
      )}
      <div
        className="shortlist-candidate-slim-details-container cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          onOpen?.()
        }}
      >
        <div className="left">
          <div className="candidate-slim-details-checkbox-container">
            <WooCheckbox
              className="candidate-slim-details-checkbox"
              resetStyle
              checked={checked}
              // Upstream's `ng-click="onCandidateCheckedClicked($event)"` stops the row's own
              // click handler firing, so ticking a box does not also open the candidate.
              stopPropagation
              onChange={(next) => onCheck?.(next)}
            />
          </div>
        </div>
        <div className="right">
          <div className="candidate-slim-details-top-section">
            {messagingEnabled ? (
              // `ng-click="ctrl.open…MessagesTooltip(…, $event)"` — the Message History.
              // ⚠️ THE CLICK IS STOPPED EVEN WHEN THERE IS NO HISTORY. Upstream's handler calls
              // `stopPropagation()` before its `!lastReceivedMessage` early return, so a click on
              // "Never" does nothing at all — it does not fall through and open the row.
              <div
                onClick={(event) => {
                  event.stopPropagation()
                  if (!candidate.lastReceivedMessage) return
                  onOpenMessages?.()
                }}
              >
                <i className="fas fa-envelope" /> {lastMessageDisplay(candidate.lastReceivedMessage)}
              </div>
            ) : null}
            <div className="candidate-slim-actioned">
              {/* Mutually exclusive: a candidate added to a job shows the briefcase INSTEAD of
                  the upload, even when both are true. */}
              {candidate.addedToJob === true ? <i className="fas fa-briefcase" title="Added to job" /> : null}
              {candidate.exported === true && candidate.addedToJob !== true ? (
                <i className="fas fa-upload" title="Exported" />
              ) : null}
            </div>
            <div className="alert-abbreviated-cv-date">
              {candidate.cvDate === undefined || candidate.cvDate === null
                ? null
                : timeSince(candidate.cvDate, true, now)}
            </div>
          </div>
          <div className="candidate-slim-details-info-section">
            <div className="candidate-slim-details-name-experience-info-section">
              {/* The tooltip is the FULL name and the text is trimmed to 50 — two different
                  calls upstream, not one value used twice. */}
              <div
                className={`cursor-pointer shortlist-candidate-slim-name algo-${String(candidate.algorithm ?? '')}`}
                title={candidateName(candidate)}
              >
                {trimCandidateName(candidate)}
                {/* ⚠️ THE SCORE AS WELL AS THE FLAG. An EXTERNAL-source row has no
                    `algorithm` — it is Woo's own match score and the source does not compute
                    one — so the flag alone rendered an empty "()" after every name on a
                    Monster list. */}
                {showAlgorithm && candidate.algorithm !== undefined && candidate.algorithm !== null ? (
                  <span> ({candidate.algorithm})</span>
                ) : null}
              </div>
              <div className="shortlist-candidate-slim-info-item info-item-job-title">{candidate.lastPosition}</div>
              {candidate.lastCompany === undefined || candidate.lastCompany === null || candidate.lastCompany === '' ? null : (
                <div className="shortlist-candidate-slim-info-item info-item-company">at {candidate.lastCompany}</div>
              )}
              {candidate.location === undefined || candidate.location === null || candidate.location === '' ? null : (
                // `into-item-location` — upstream's typo, and the stylesheet is written against
                // it. Correcting the spelling here would silently drop the rule.
                <div className="shortlist-candidate-slim-info-item into-item-location" title={candidate.location}>
                  <i className="fa fa-map-marker" />
                  {'  '}
                  {candidate.location}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* `alert-candidate-bottom-section` is the second class upstream puts here, and the
          stylesheet uses it: `.shortlist-candidate-slim-hover` is the reveal, the other is the
          spacing. Rendered only when there is something to render — an empty strip would add
          the hover gap under every row for no icons. */}
      {checkboxDependent || actions === undefined || actions.length === 0 ? null : (
        <div className="shortlist-candidate-slim-hover alert-candidate-bottom-section">
          <ProfileActionsStrip entity={candidate} actions={actions} maxActionAmount={6} onAction={onAction} />
        </div>
      )}
    </div>
  )
}
